import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';
import { Button, IconMain, ImgMain } from '#components';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { toShortAddress } from '#imports';
import type { EvmFullFillCreateOrderListing, OrderListParams } from '~/service/api/types';
import type { Address, Hash } from 'viem';
import type { EvmConsideration } from '~/service/EVM/types';

dayjs.extend(duration);

const columnHelper = createColumnHelper<EvmFullFillCreateOrderListing>();

export function useReceivedOfferColumns({ user, onSort, onShowModal }: { user?: Address; onShowModal: (data: Pick<EvmFullFillCreateOrderListing, 'order' | 'price' | 'source' | 'nftInfo' | 'orderHash' | 'tokensRemaining'>) => void; onSort: (param: OrderListParams['sort']) => void }) {
    return [
        columnHelper.accessor('price.amount', {
            header: () => h(
                'div',
                {
                    class: 'display-flex flex-nowrap cursor-pointer',
                    onClick: () => onSort('price.price')
                },
                [
                    'Price',
                    h(
                        IconMain,
                        {
                            size: 16,
                            icon: 'up-down'
                        }
                    )
                ]
            ),
            cell: (info) => {
                const { row } = info;
                return h('div', { class: 'display-flex align-items-center gap-4' }, [
                    h(ImgMain, {
                        src: info.row.original.price?.logo,
                        alt: info.row.original.nftInfo.name,
                        quality: '90',
                        densities: 'x1 x2',
                        class: 'logo',
                        width: 24,
                        height: 24,
                        size: '24px',
                        loading: 'lazy'
                    }),
                    h('span', { class: 'compact-400-accent-numeric' }, row.original.price?.amount)
                ]);
            }
        }),
        columnHelper.accessor('order.parameters.consideration', {
            header: 'Quantity',
            cell: info =>
                h('span', { class: 'compact-400-accent' }, calcQuantity(info.row.original.order.parameters.consideration, info.row.original.order.parameters.offerer))
        }),
        columnHelper.accessor('order.parameters.endTime', {
            header: () => h(
                'div',
                {
                    class: 'display-flex flex-nowrap cursor-pointer',
                    onClick: () => onSort('endTime')
                },
                [
                    'Expiration',
                    h(
                        IconMain,
                        {
                            size: 16,
                            icon: 'up-down'
                        }
                    )
                ]
            ),
            cell: info =>
                h('span', { class: 'compact-400-accent' }, getTimeLeft(String(info.row.original.order.parameters.endTime)))
        }),
        columnHelper.accessor('order.parameters.offerer', {
            header: 'From',
            cell: info =>
                h('span', { class: 'compact-400-accent text-color-brand-aviation' }, toShortAddress(info.row.original.order.parameters.offerer))
        })
        // columnHelper.accessor('order.parameters.offerer', {
        //     header: '',
        //     cell: (info) => {
        //         if (user) {
        //             return h(ClientOnly, {}, {
        //                 default: () => h(OfferCancelButton, {
        //                     orderHash: info.row.original.orderHash,
        //                     onShowModal: () => onShowModal({
        //                         price: info.row.original.price,
        //                         source: info.row.original.source,
        //                         tokensRemaining: info.row.original.tokensRemaining,
        //                         nftInfo: info.row.original.nftInfo,
        //                         order: info.row.original.order,
        //                         orderHash: info.row.original.orderHash
        //                     })
        //                 }),
        //                 fallback: () => h(LoaderDots)
        //             });
        //         }
        //     }
        // })
    ];
}

const OfferCancelButton = defineComponent({
    props: {
        orderHash: {
            type: String as PropType<Hash>,
            required: true
        }
    },
    emits: ['showModal'],
    setup(props, { emit }) {
        return () =>
            h(
                Button,
                {
                    color: 'bg.secondary',
                    size: 'size.small',
                    class: 'text-center',
                    style: { minHeight: '39px' },
                    onClick: () => emit('showModal')
                },
                { default: () => h('span', {}, 'Respond') }
            );
    }
});

function calcQuantity(considerations: EvmConsideration[], offerer: Address) {
    return considerations.filter(filterItem => filterItem.recipient === offerer).reduce((sum, item) => sum + Number(item.endAmount), 0);
}

function getTimeLeft(dateString: string): string {
    const now = dayjs();
    const target = dayjs.unix(Number(dateString));
    const diff = target.diff(now);
    if (diff <= 0) return 'time is up';

    const timeLeft = dayjs.duration(diff);
    if (timeLeft.asDays() >= 1) {
        return `${Math.floor(timeLeft.asDays())} d`;
    }
    else if (timeLeft.asHours() >= 1) {
        return `${Math.floor(timeLeft.asHours())} h`;
    }
    else {
        return `${Math.floor(timeLeft.asMinutes())} min`;
    }
}
